import {
  Box,
  Button,
  Divider,
  Heading,
  Link,
  Modal,
  ModalBody,
  Text,
  TextArea,
  TextInput,
} from '@razorpay/blade/components';
import { salaryComponentParams } from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import {
  knowMoreItems,
  nonPayableBenefitsGeneralDetailsStepValidationSchema,
} from 'components/WizardViews/SalaryComponents/constants';
import { NonPayableBenefitsFormData } from 'components/WizardViews/SalaryComponents/types';
import {
  FieldGroup,
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
  SwitchDescription,
  SwitchField,
} from 'components/WizardViews/components';
import { useForm } from 'forms';
import React, { FormEvent, useState } from 'react';

const GeneralDetailsStep: React.FC = () => {
  const { formData, validate, register } = useForm<NonPayableBenefitsFormData>();
  const { openDetailsView } = useDetailsView(salaryComponentParams.knowMore);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Add validation schema if needed
    if (validate(nonPayableBenefitsGeneralDetailsStepValidationSchema)) {
      setIsModalOpen(true);
    }
  };

  const handleConfirm = () => {
    setIsSubmitting(true);
    // Here you would typically make an API call to create the component
    setTimeout(() => {
      setIsSubmitting(false);
      setIsModalOpen(false);
    }, 1000);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper>
        <StepHeader title="General" subtitle="Configuration" marginBottom={'spacing.9'} />

        <FieldGroup title="General details">
          <TextInput
            label="Component name"
            placeholder="Enter component name"
            {...register('name')}
            isRequired
          />

          <TextInput
            label="Display name in payslip & Salary Register"
            placeholder="Enter display name"
            helpText="Display will be used in employee payslip and salary register"
            {...register('displayName')}
            isRequired
          />

          <TextArea
            label="Component description"
            placeholder="Enter description"
            {...register('description')}
          />
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup title="Part of salary structure">
          <SwitchField
            label="Component can be part of salary structure"
            accessibilityLabel="Include in CTC"
            size="small"
            {...register('isPartOfCTC', 'switch')}>
            <SwitchDescription content="Use component to add to a salary structure and assign it to employees." />
            <Link
              variant={'button'}
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.proration)}
              accessibilityLabel={'know more - proration'}>
              Know more✨
            </Link>
          </SwitchField>
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup title="Employee visibility">
          <SwitchField
            label="Visible to employee as part of their compensation"
            accessibilityLabel="Visible to employee"
            size="small"
            tooltip={
              formData.isPartOfCTC
                ? 'The component that is part of the CTC will always be visible to employees on their compensation'
                : ''
            }
            {...register('visibilityToEmployee', 'switch')}
            isDisabled={formData.isPartOfCTC}
            isChecked={formData.isPartOfCTC ? true : formData.visibilityToEmployee}>
            <SwitchDescription content="Component will be visible to employee in their compensation." />
            <Link
              variant={'button'}
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.proration)}
              accessibilityLabel={'know more - proration'}>
              Know more✨
            </Link>
          </SwitchField>
        </FieldGroup>
      </StepContentWrapper>
      <Modal
        isOpen={isModalOpen}
        onDismiss={handleCancel}
        size="small"
        accessibilityLabel="Create component">
        <ModalBody padding="spacing.6">
          <Heading size="large">Create component</Heading>
          <Text weight="regular" marginY="spacing.4">
            A new Non-Payable benefit component named "{formData.name}" will be created.
          </Text>
          <Text marginY="spacing.7" weight="regular">
            Once created, you can use this component in a salary structure and assign it to
            employees.
          </Text>
          <Box display="flex" justifyContent="flex-end" gap="spacing.3">
            <Button variant="tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} isLoading={isSubmitting}>
              Confirm
            </Button>
          </Box>
        </ModalBody>
      </Modal>

      <StepActionsWrapper>
        <span />
        <Button type="submit">Create component</Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default GeneralDetailsStep;
